import React, {
  useMemo,
  useState,
} from 'react'
import {
  ActionIcon,
  Badge,
  Collapse,
  Group,
  Paper,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core'
import {
  Check,
  ChevronDown,
  ChevronRight,
  Mail,
  RefreshCw,
} from 'lucide-react'
import { format } from 'date-fns'
import { EmailHistoryModal } from './EmailHistoryModal'
import { useMailEvents } from '@data/mail/mail'

interface EmailEventDisplayProps {
  gid: string
}

interface RecipientEvent {
  email: string
  event: string
  createdAt: string
}

const eventColors: Record<string, string> = {
  created: 'gray',
  received: 'blue',
  processed: 'cyan',
  delivered: 'green',
  opened: 'teal',
  clicked: 'violet',
  bounced: 'red',
  dropped: 'red',
  deferred: 'orange',
  failed: 'red',
}

export const EmailEventDisplay = ({ gid }: EmailEventDisplayProps) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false)
  const { data: mailEvents, isFetching, isPending, refetch } = useMailEvents({ gid: gid })

  const recipientEventsMap = useMemo(() => {
    const map = new Map<string, RecipientEvent>()

    mailEvents?.forEach(event => {
      const existing = map.get(event.email)
      if (!existing || new Date(event.createdAt) > new Date(existing.createdAt)) {
        map.set(event.email, {
          email: event.email,
          event: event.event,
          createdAt: event.createdAt,
        })
      }
    })
    return map
  }, [mailEvents])

  const recipientEvents = Array.from(recipientEventsMap.values())
  const totalRecipients = recipientEvents.length

  const handleEventBadgeClick = (e: React.MouseEvent) => {
    e.stopPropagation() // Prevent the collapse toggle
    setIsHistoryModalOpen(true)
  }

  const handleRefreshMailClick = async (e: React.MouseEvent) => {
    e.stopPropagation()
    await refetch()
  }

  if (!recipientEvents.length) {
    return null
  }

  return (
    <>
      {mailEvents ?
        <EmailHistoryModal
          opened={isHistoryModalOpen}
          onClose={() => setIsHistoryModalOpen(false)}
          mailEvents={mailEvents}
        /> : null}
      <Paper
        p={'sm'}
        mt={'md'}
        withBorder
        style={{
          backgroundColor: '#e7f5ff',
          borderLeft: '3px solid #228be6',
        }}
      >
        <Group
          justify={'space-between'}
          style={{ cursor: 'pointer' }}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <Group gap={'xs'}>
            <Tooltip label='Refresh Mail Events' disabled={isPending || isFetching}>
              <ActionIcon
                variant='light'
                color='blue'
                loading={isFetching || isPending}
                onClick={handleRefreshMailClick}
                aria-label='Refresh Mail Events'
              >
                <RefreshCw size={16} />
              </ActionIcon>
            </Tooltip>
            <Mail size={14} color={'blue'} />
            <Text
              size={'sm'}
              fw={600}
              c={'blue'}
            >
              Email sent to {totalRecipients} recipient{totalRecipients > 1 ? 's' : ''}
            </Text>
            <Badge
              size={'sm'}
              variant={'light'}
              color={'blue'}
              style={{ cursor: 'pointer' }}
              onClick={handleEventBadgeClick}
            >
              {recipientEvents.length} event{recipientEvents.length > 1 ? 's' : ''}
            </Badge>
          </Group>
          <ActionIcon
            variant={'subtle'}
            size={'sm'}
            color={'blue'}
          >
            {isExpanded
              ? <ChevronDown size={16} />
              : <ChevronRight size={16} />}
          </ActionIcon>
        </Group>
        <Collapse expanded={isExpanded}>
          <Stack gap={'xs'} mt={'md'}>
            <Text
              size={'xs'}
              c={'dimmed'}
              fw={600}
            >
              Most Recent Status per Recipient:
            </Text>
            {recipientEvents.map((recipient) => (
              <Paper
                key={recipient.email}
                p={'xs'}
                withBorder
                style={{ backgroundColor: '#fff' }}
              >
                <Group justify={'space-between'} align={'flex-start'}>
                  <div style={{ flex: 1 }}>
                    <Group gap={'xs'} mb={4}>
                      <Text size={'sm'} fw={500}>
                        {recipient.email}
                      </Text>
                      <Badge
                        size={'sm'}
                        variant={'light'}
                        color={eventColors[recipient.event] || 'gray'}
                        leftSection={recipient.event === 'delivered' ? <Check size={10} /> : undefined}
                      >
                        {recipient.event}
                      </Badge>
                    </Group>
                    <Text size={'xs'} c={'dimmed'}>
                      {format(new Date(recipient.createdAt), 'dd-MM-yyyy HH:mm:ss')}
                    </Text>
                  </div>
                </Group>
              </Paper>
            ))}
          </Stack>
        </Collapse>
      </Paper>
    </>
  )
}
